/**
 * Sections of the agency member detail page, in display order. The active one
 * lives in the URL (`?tab=access`) so deep links and the back button work;
 * `profile` is the default and is never written to the URL.
 */
export const AGENCY_MEMBER_DETAIL_TABS = ['profile', 'access', 'activity', 'account'] as const;

export type AgencyMemberDetailTab = (typeof AGENCY_MEMBER_DETAIL_TABS)[number];

/**
 * Sections of a MEMBER-role viewer's own profile (`/agency/profile`). Activity
 * is admin-only (the API refuses it), so it is left out.
 */
export const AGENCY_SELF_PROFILE_TABS = ['profile', 'access', 'account'] as const satisfies readonly AgencyMemberDetailTab[];

// Literal type so it also defaults the narrower self-profile tab union.
export const DEFAULT_AGENCY_MEMBER_DETAIL_TAB = 'profile' satisfies AgencyMemberDetailTab;

export const AGENCY_MEMBER_DETAIL_TAB_LABELS: Record<AgencyMemberDetailTab, string> = {
  profile: 'Profile',
  access: 'Access',
  activity: 'Activity',
  account: 'Account',
};

export function isAgencyMemberDetailTab(value: unknown): value is AgencyMemberDetailTab {
  return typeof value === 'string' && (AGENCY_MEMBER_DETAIL_TABS as readonly string[]).includes(value);
}

export type AgencyMemberDetailSearchParams = {
  tab?: AgencyMemberDetailTab;
};

/** Route `validateSearch`: keeps a known non-default tab, drops anything else. */
export function parseAgencyMemberDetailSearch(search: Record<string, unknown>): AgencyMemberDetailSearchParams {
  return isAgencyMemberDetailTab(search.tab) && search.tab !== DEFAULT_AGENCY_MEMBER_DETAIL_TAB
    ? { tab: search.tab }
    : {};
}

export type AgencySelfProfileTab = (typeof AGENCY_SELF_PROFILE_TABS)[number];

export function isAgencySelfProfileTab(value: unknown): value is AgencySelfProfileTab {
  return typeof value === 'string' && (AGENCY_SELF_PROFILE_TABS as readonly string[]).includes(value);
}
